import { useDispatch, useSelector } from 'react-redux';
import React, { useState, useEffect, useMemo, JSX } from 'react';
import { PATHS } from 'src/common/constants';
import { variables } from 'src/Tenants';
import Breadcrumb from '../../../components/Breadcrumb';
import Button from '../../../components/Button';
import Details from '../../../components/Details';
import FontAwesomeIcon from '../../../components/Icon';
import { deleteContactById } from 'src/store/actions/contacts';
import {
  onClearMoreDetails,
  onFetchContactMoreDetails,
} from 'src/store/actions/suites';
import Loader from '../../../components/Loader';
import ContactMoreDetails from '../ContactMoreDetails';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { useHistory } from 'react-router-dom';
import '../ContactDetails.scss';
import {
  ContactFormActionsContext,
  useContactFormActionsFromContext,
} from 'src/SharedCommonContext/ContactFormContext';
import ContactForm from 'src/components/ContactForm/ContactForm';
import { enrichContactDataForEdit } from 'src/utils/CommonHelpers/commonhelpers';
import {
  useContactFormSubmissionContacts
} from 'src/pages/SharedPageCustomHooks/UseContactSaveContactsPage/useContactFormSubmission';
import { buildContactDetailsItems } from 'src/pages/Contact/ContactDetails/helpers';
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal';
import ActivationLinkCard from 'src/components/ActivationLinkCard/ActivationLinkCard';

const ContactDetails = ({
  contactData,
  sendResetPasswordLinkToUser,
  sendActivationLinkToUser,
  navFrom,
  navOrigin,
  counts,
  isFromDashBoard,
  refetchContactFormData,
  loadingContact,
  loadingCounts,
  contactId,
}: any) => {
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

  const {
    clearContactFormData,
    initializeContactFormDataFromEdit,
  }: ContactFormActionsContext = useContactFormActionsFromContext();

  const dispatch = useDispatch();
  const history = useHistory();

  const moreDetails = useSelector((state: any) => state.moreDetails);
  const user = useSelector((state: any) => selectAuthenticatedUser(state));

  const onDeleteContact = (contact: any) => {
    dispatch(deleteContactById(contact.id, history));
  };

  useEffect(() => {
    dispatch(onClearMoreDetails());
  }, []);

  const onFetchMoreDetails = () => {
    if (!contactData) {
      return;
    }
    dispatch(onFetchContactMoreDetails(contactData.id));
  };

  const handleEditClick = () => {
    if (!contactData) {
      return;
    }
    setShowOverlay(true);
    const enrichedContactData: any = enrichContactDataForEdit(contactData);
    initializeContactFormDataFromEdit(enrichedContactData);
  };

  const handleCloseForm = () => {
    setShowOverlay(false);
    clearContactFormData();
  };

  const { handleSubmitContactForm } = useContactFormSubmissionContacts({
    onSuccess: () => refetchContactFormData(contactId),
    onClose: handleCloseForm,
  });

  const currentUserRole: string = user?.roles?.[0]?.role?.name ?? '';
  const contactStatus: string = contactData?.status ?? '';

  const detailsItems: JSX.Element[] = useMemo(
    () =>
      buildContactDetailsItems({
        contactData,
        counts,
        user,
        isOpen,
        setIsOpen,
        onFetchMoreDetails,
        loadingContact,
        loadingCounts,
      }),
    [contactData, counts, user, isOpen, loadingContact, loadingCounts]
  );

  return (
    <div className="contact-details">
      <div className="contact-details__top">
        <div className="contact-details__breadcrumb">
          {isFromDashBoard === true ? (
            <Breadcrumb
              label="MY HOME"
              to={{
                pathname: PATHS.SUITES_VIEW.replace(
                  ':id',
                  localStorage.getItem('propertyId') +
                    '&' +
                    localStorage.getItem('suiteId')
                ),
                state: {
                  suite: { id: localStorage.getItem('suiteId') },
                  property: { id: localStorage.getItem('propertyId') },
                  activeTab: 0,
                  from: 'Dashboard',
                },
              }}
            />
          ) : navOrigin?.kind === 'suite' &&
            navOrigin.propertyId &&
            navOrigin.suiteId ? (
            <Breadcrumb
              label={
                navOrigin.suiteNumber
                  ? `${variables.Suite} ${navOrigin.suiteNumber}`
                  : variables.Suite
              }
              to={{
                pathname: PATHS.SUITES_VIEW.replace(
                  ':id',
                  `${navOrigin.propertyId}&${navOrigin.suiteId}`
                ),
                state: {
                  suite: { id: navOrigin.suiteId },
                  property: { id: navOrigin.propertyId },
                },
              }}
            />
          ) : navOrigin?.kind === 'property' && navOrigin.propertyId ? (
            <Breadcrumb
              label={navOrigin.propertyName || 'Property'}
              to={PATHS.PROPERTIES_VIEW.replace(':id', navOrigin.propertyId)}
            />
          ) : navOrigin?.kind === 'serviceRequest' &&
            navOrigin.serviceRequestId ? (
            <Breadcrumb
              label="Service Request"
              to={PATHS.SERVICE_REQUESTS_VIEW.replace(
                ':id',
                navOrigin.serviceRequestId
              )}
            />
          ) : navFrom !== '' ? (
            <Breadcrumb
              label="Service Request"
              to={PATHS.SERVICE_REQUESTS_VIEW.replace(':id', navFrom)}
            />
          ) : (
            <Breadcrumb label="Contacts" to={PATHS.CONTACTS_LIST} />
          )}
        </div>
      </div>
      <div className="contact-details__bottom">
        <div className="contact-details__actions">
          <Button
            color="semantic"
            onClick={handleEditClick}
            disabled={loadingContact}
            label={<FontAwesomeIcon glyph="pencil" weight="light" />}
          />
          <Button
            color="semantic"
            onClick={() => setIsDeleteModalOpen(true)}
            disabled={loadingContact}
            label={<FontAwesomeIcon glyph="trash" weight="light" />}
          />
        </div>
        <Details items={detailsItems} />
        {contactData && (
          <ActivationLinkCard
            userId={contactData.id}
            status={contactStatus}
            viewerRole={currentUserRole}
          />
        )}
      </div>
      {isDeleteModalOpen && (
        <ConfirmationModal
          variant="danger"
          title={`Delete Contact ${contactData.firstname}?`}
          confirmLabel="Yes, Delete Permanently"
          cancelLabel="Go Back"
          requireTypeToConfirm={true}
          confirmationWord="DELETE"
          message={
            <div>
              <p>
                You are about to <strong>permanently delete</strong> this
                contact profile.
              </p>
              <p style={{ marginTop: '8px' }}>
                This will remove{' '}
                <strong>
                  {contactData.firstname} {contactData.lastname}
                </strong>{' '}
                from the system entirely, including:
              </p>
              <ul
                style={{
                  textAlign: 'left',
                  display: 'inline-block',
                  marginTop: '8px',
                }}
              >
                <li>Login access for this user</li>
                <li>Associated activity history and notes</li>
                <li>All profile details and documents</li>
              </ul>
            </div>
          }
          warningMessage="This action is irreversible. All data for this contact will be lost forever."
          onCancel={() => setIsDeleteModalOpen(false)}
          onConfirm={() => {
            onDeleteContact(contactData);
            setIsDeleteModalOpen(false);
          }}
        />
      )}
      {showOverlay && (
        <ContactForm
          onClose={handleCloseForm}
          onSubmit={handleSubmitContactForm}
          sendResetPasswordLinkToUser={sendResetPasswordLinkToUser}
          sendActivationLinkToUser={sendActivationLinkToUser}
          pageUsedFrom="Contacts"
        />
      )}
      {moreDetails && moreDetails.isEnableLoader ? (
        <Loader center={true} />
      ) : null}
      {moreDetails && moreDetails.isMoreDetails ? (
        <ContactMoreDetails
          closePopup={() => dispatch(onClearMoreDetails())}
          details={moreDetails.moreData}
        />
      ) : null}
    </div>
  );
};

export default ContactDetails;